import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, X } from "lucide-react";
import { AdminChatTab } from "../components/AdminChatTab";

const STATUS_OPTIONS = [
    { id: "pending", label: "Ελήφθη" },
    { id: "preparing", label: "Ετοιμάζεται" },
    { id: "on_the_way", label: "Στον δρόμο" },
    { id: "delivered", label: "Παραδόθηκε" },
    { id: "cancelled", label: "Ακυρώθηκε" },
];

const TABS = [
    { id: "orders", label: "Παραγγελίες" },
    { id: "chat", label: "Chat" },
    { id: "menu", label: "Μενού" },
    { id: "coupons", label: "Κουπόνια" },
];

const empty = { name: "", description: "", price: 0, category: "burgers", image_url: "", available: true, featured: false };

export const Admin = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("orders");
    const [chatUnread, setChatUnread] = useState(0);

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") navigate("/");
    }, [user, loading, navigate]);

    const fetchUnread = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/chat/unread-count");
            setChatUnread(data.unread_conversations || 0);
        } catch {}
    }, []);

    useEffect(() => {
        if (!user || user.role !== "admin") return;
        fetchUnread();
        const t = setInterval(fetchUnread, 5000);
        return () => clearInterval(t);
    }, [user, fetchUnread]);

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-12" data-testid="admin-page">
            <h1 className="headline text-4xl sm:text-5xl mb-8">Admin Panel</h1>
            <div className="flex gap-2 border-b border-white/10 mb-8 overflow-x-auto">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        data-testid={`admin-tab-${t.id}`}
                        onClick={() => { setTab(t.id); if (t.id === "chat") fetchUnread(); }}
                        className={`relative px-4 py-3 font-heading uppercase tracking-wider text-sm border-b-2 -mb-px whitespace-nowrap ${
                            tab === t.id ? "border-brand-red text-white" : "border-transparent text-white/50 hover:text-white"
                        }`}
                    >
                        {t.label}
                        {t.id === "chat" && chatUnread > 0 && (
                            <span data-testid="admin-chat-unread" className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1 bg-brand-red text-white text-[10px] font-bold rounded-full">
                                {chatUnread}
                            </span>
                        )}
                    </button>
                ))}
            </div>
            {tab === "orders" && <OrdersTab />}
            {tab === "chat" && <AdminChatTab />}
            {tab === "menu" && <MenuTab />}
            {tab === "coupons" && <CouponsTab />}
        </div>
    );
};

const OrdersTab = () => {
    const [orders, setOrders] = useState([]);
    const fetchAll = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
    useEffect(() => { fetchAll(); const t = setInterval(fetchAll, 10000); return () => clearInterval(t); }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/orders/${id}/status`, { status });
            toast.success("Κατάσταση ενημερώθηκε");
            fetchAll();
        } catch { toast.error("Σφάλμα"); }
    };

    return (
        <div className="overflow-x-auto" data-testid="admin-orders">
            <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-white/50 border-b border-white/10">
                    <tr><th className="py-3 pr-4">#</th><th className="pr-4">Πελάτης</th><th className="pr-4">Σύνολο</th><th className="pr-4">Πληρωμή</th><th className="pr-4">Κατάσταση</th><th>Ενέργειες</th></tr>
                </thead>
                <tbody>
                    {orders.map((o) => (
                        <tr key={o.id} className="border-b border-white/5">
                            <td className="py-3 pr-4 font-mono text-xs">{o.id.slice(0, 8)}</td>
                            <td className="pr-4">{o.customer_name}<div className="text-xs text-white/40">{o.phone}</div></td>
                            <td className="pr-4 text-brand-yellow font-bold">€{o.total.toFixed(2)}</td>
                            <td className="pr-4 text-xs uppercase">{o.payment_method} / {o.payment_status}</td>
                            <td className="pr-4 uppercase text-xs">{STATUS_OPTIONS.find((s) => s.id === o.status)?.label}</td>
                            <td>
                                <select
                                    data-testid={`order-status-select-${o.id}`}
                                    value={o.status}
                                    onChange={(e) => updateStatus(o.id, e.target.value)}
                                    className="bg-[#0c0c0e] border border-white/15 px-2 py-1 text-xs"
                                >
                                    {STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                                </select>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {orders.length === 0 && <p className="text-white/50 py-12 text-center">Καμία παραγγελία ακόμα.</p>}
        </div>
    );
};

const MenuTab = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null | "new" | itemId
    const [form, setForm] = useState(empty);

    const fetchAll = () => api.get("/menu").then(({ data }) => setItems(data));
    useEffect(() => { fetchAll(); }, []);

    const startNew = () => { setForm(empty); setEditing("new"); };
    const startEdit = (it) => { setForm({ ...it }); setEditing(it.id); };
    const cancel = () => { setEditing(null); setForm(empty); };

    const save = async () => {
        try {
            const payload = { ...form, price: parseFloat(form.price) };
            if (editing === "new") {
                await api.post("/menu", payload);
                toast.success("Προστέθηκε");
            } else {
                await api.put(`/menu/${editing}`, payload);
                toast.success("Ενημερώθηκε");
            }
            cancel(); fetchAll();
        } catch (err) { toast.error(err.response?.data?.detail || "Σφάλμα"); }
    };

    const del = async (id) => {
        if (!window.confirm("Διαγραφή προϊόντος;")) return;
        await api.delete(`/menu/${id}`);
        toast.success("Διαγράφηκε"); fetchAll();
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    return (
        <div data-testid="admin-menu">
            <div className="flex justify-end mb-4">
                <button data-testid="add-menu-btn" onClick={startNew} className="btn-primary !py-2 !px-4 text-xs flex items-center gap-2">
                    <Plus size={14} /> Νέο προϊόν
                </button>
            </div>
            {editing && (
                <div className="bg-[#16161a] border border-white/10 p-6 mb-6 grid sm:grid-cols-2 gap-3" data-testid="menu-form">
                    <input placeholder="Όνομα" value={form.name} onChange={set("name")} className="bg-[#0c0c0e] border border-white/15 px-3 py-2 rounded-sm" data-testid="menu-form-name" />
                    <input type="number" step="0.01" placeholder="Τιμή" value={form.price} onChange={set("price")} className="bg-[#0c0c0e] border border-white/15 px-3 py-2 rounded-sm" data-testid="menu-form-price" />
                    <select value={form.category} onChange={set("category")} className="bg-[#0c0c0e] border border-white/15 px-3 py-2 rounded-sm">
                        <option value="burgers">Burgers</option><option value="sides">Sides</option><option value="drinks">Drinks</option><option value="desserts">Desserts</option>
                    </select>
                    <input placeholder="URL Εικόνας" value={form.image_url} onChange={set("image_url")} className="bg-[#0c0c0e] border border-white/15 px-3 py-2 rounded-sm" />
                    <textarea placeholder="Περιγραφή" value={form.description} onChange={set("description")} rows={2} className="sm:col-span-2 bg-[#0c0c0e] border border-white/15 px-3 py-2 rounded-sm" />
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.available} onChange={set("available")} /> Διαθέσιμο</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={set("featured")} /> Featured</label>
                    <div className="sm:col-span-2 flex gap-2">
                        <button onClick={save} className="btn-primary flex-1" data-testid="menu-form-save">Αποθήκευση</button>
                        <button onClick={cancel} className="btn-outline">Άκυρο</button>
                    </div>
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-white/50 border-b border-white/10">
                        <tr><th className="py-3 pr-4">Όνομα</th><th className="pr-4">Κατηγορία</th><th className="pr-4">Τιμή</th><th className="pr-4">Διαθέσιμο</th><th></th></tr>
                    </thead>
                    <tbody>
                        {items.map((i) => (
                            <tr key={i.id} className="border-b border-white/5">
                                <td className="py-3 pr-4">{i.name}</td>
                                <td className="pr-4 capitalize">{i.category}</td>
                                <td className="pr-4">€{i.price.toFixed(2)}</td>
                                <td className="pr-4">{i.available ? "✓" : "—"}</td>
                                <td className="flex gap-2 py-3">
                                    <button onClick={() => startEdit(i)} className="text-white/60 hover:text-white" data-testid={`edit-${i.id}`}><Pencil size={14} /></button>
                                    <button onClick={() => del(i.id)} className="text-white/60 hover:text-brand-red" data-testid={`del-${i.id}`}><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const CouponsTab = () => {
    const [coupons, setCoupons] = useState([]);
    const [form, setForm] = useState({ code: "", type: "percent", value: 10, min_order: 0, active: true });

    const fetchAll = () => api.get("/coupons").then(({ data }) => setCoupons(data));
    useEffect(() => { fetchAll(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/coupons", { ...form, value: parseFloat(form.value), min_order: parseFloat(form.min_order) });
            toast.success("Δημιουργήθηκε");
            setForm({ code: "", type: "percent", value: 10, min_order: 0, active: true });
            fetchAll();
        } catch (err) { toast.error(err.response?.data?.detail || "Σφάλμα"); }
    };

    const del = async (id) => { await api.delete(`/coupons/${id}`); fetchAll(); };
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    return (
        <div data-testid="admin-coupons">
            <form onSubmit={create} className="bg-[#16161a] border border-white/10 p-6 mb-6 grid sm:grid-cols-5 gap-3">
                <input required placeholder="Κωδικός" value={form.code} onChange={set("code")} className="bg-[#0c0c0e] border border-white/15 px-3 py-2 rounded-sm uppercase" data-testid="coupon-code" />
                <select value={form.type} onChange={set("type")} className="bg-[#0c0c0e] border border-white/15 px-3 py-2 rounded-sm">
                    <option value="percent">% Έκπτωση</option><option value="fixed">€ Έκπτωση</option>
                </select>
                <input type="number" step="0.01" required placeholder="Τιμή" value={form.value} onChange={set("value")} className="bg-[#0c0c0e] border border-white/15 px-3 py-2 rounded-sm" data-testid="coupon-value" />
                <input type="number" step="0.01" placeholder="Ελάχιστη παραγγελία" value={form.min_order} onChange={set("min_order")} className="bg-[#0c0c0e] border border-white/15 px-3 py-2 rounded-sm" />
                <button type="submit" className="btn-primary" data-testid="coupon-create">Δημιουργία</button>
            </form>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-white/50 border-b border-white/10">
                        <tr><th className="py-3 pr-4">Κωδικός</th><th className="pr-4">Τύπος</th><th className="pr-4">Αξία</th><th className="pr-4">Min.</th><th></th></tr>
                    </thead>
                    <tbody>
                        {coupons.map((c) => (
                            <tr key={c.id} className="border-b border-white/5">
                                <td className="py-3 pr-4 font-mono">{c.code}</td>
                                <td className="pr-4">{c.type}</td>
                                <td className="pr-4">{c.type === "percent" ? `${c.value}%` : `€${c.value}`}</td>
                                <td className="pr-4">€{c.min_order.toFixed(2)}</td>
                                <td><button onClick={() => del(c.id)} className="text-white/60 hover:text-brand-red" data-testid={`del-coupon-${c.id}`}><Trash2 size={14} /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
